import React, { useState, useEffect } from 'react';
import api from '../../api';
import CapacityManagementModal from './CapacityManagementModal';
import './SprintManagementModal.css';

export default function SprintManagementModal({ boardId, boardName, onClose, onSprintUpdate }) {
  const [sprints, setSprints] = useState([]);
  const [activeSprint, setActiveSprint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [boardSettings, setBoardSettings] = useState(null);
  const [selectedSprintForCapacity, setSelectedSprintForCapacity] = useState(null);
  
  // Form state
  const [sprintForm, setSprintForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    goal: '',
    is_active: false,
    planning_method: 'hours'
  });

  useEffect(() => {
    if (boardId) {
      loadSprints();
      loadBoardSettings();
      loadTasks();
    }
  }, [boardId]);

  const loadSprints = async () => {
    try {
      const res = await api.get(`/sprints/board/${boardId}`);
      const allSprints = res.data || [];
      // Sort sprints: active first, then by start date descending
      const sortedSprints = [...allSprints].sort((a, b) => {
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        return new Date(b.start_date) - new Date(a.start_date);
      });
      setSprints(sortedSprints);
      const active = allSprints.find(s => s.is_active);
      setActiveSprint(active || null);
    } catch (err) {
      console.error('Failed to load sprints:', err);
      alert('Failed to load sprints.');
    }
  };

  const loadBoardSettings = async () => {
    try {
      // TODO: Create board settings endpoint if needed
      // For now, default to hours
      setBoardSettings({ planning_method: 'hours' });
    } catch (err) {
      console.error('Failed to load board settings:', err);
    }
  };

  const loadTasks = async () => {
    try {
      const res = await api.get('/tasks');
      const boardTasks = (res.data || []).filter(t => t.team_id === boardId);
      setTasks(boardTasks);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  };

  const handleCreateSprint = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const sprintData = {
        ...sprintForm,
        board_id: boardId,
        is_active: sprintForm.is_active || false
      };

      await api.post('/sprints', sprintData);
      await loadSprints();
      
      // Reset form
      setSprintForm({
        name: '',
        start_date: '',
        end_date: '',
        goal: '',
        is_active: false,
        planning_method: 'hours'
      });
      setShowCreateForm(false);
      
      if (onSprintUpdate) {
        onSprintUpdate();
      }
    } catch (err) {
      console.error('Failed to create sprint:', err);
      alert('Failed to create sprint. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSprint = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.put(`/sprints/${editingSprint.id}`, sprintForm);
      await loadSprints();
      setEditingSprint(null);
      setSprintForm({
        name: '',
        start_date: '',
        end_date: '',
        goal: '',
        is_active: false,
        planning_method: 'hours'
      });
      
      if (onSprintUpdate) {
        onSprintUpdate();
      }
    } catch (err) {
      console.error('Failed to update sprint:', err);
      alert('Failed to update sprint. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSprint = async (sprintId) => {
    if (!window.confirm('Are you sure you want to delete this sprint? This will remove all tasks from the sprint.')) {
      return;
    }

    try {
      await api.delete(`/sprints/${sprintId}`);
      await loadSprints();
      
      if (onSprintUpdate) {
        onSprintUpdate();
      }
    } catch (err) {
      console.error('Failed to delete sprint:', err);
      alert('Failed to delete sprint. Please try again.');
    }
  };

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // If it's an ISO string or Date object, extract YYYY-MM-DD
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleEditSprint = (sprint) => {
    setEditingSprint(sprint);
    setSprintForm({
      name: sprint.name,
      start_date: formatDateForInput(sprint.start_date),
      end_date: formatDateForInput(sprint.end_date),
      goal: sprint.goal || '',
      is_active: sprint.is_active || false,
      planning_method: sprint.planning_method || 'hours'
    });
    setShowCreateForm(false);
  };

  const handleAddTaskToSprint = async (sprintId, taskId, hours, points) => {
    try {
      await api.post(`/sprints/${sprintId}/tasks`, {
        task_id: taskId,
        estimated_hours: hours || null,
        estimated_points: points || null
      });
      await loadSprints();
    } catch (err) {
      console.error('Failed to add task to sprint:', err);
      alert('Failed to add task to sprint.');
    }
  };

  const handleRemoveTaskFromSprint = async (sprintId, taskId) => {
    try {
      await api.delete(`/sprints/${sprintId}/tasks/${taskId}`);
      await loadSprints();
    } catch (err) {
      console.error('Failed to remove task from sprint:', err);
      alert('Failed to remove task from sprint.');
    }
  };

  const getSprintTasks = (sprintId) => {
    // This would need to be loaded from the sprint detail endpoint
    // For now, return empty array
    return [];
  };

  return (
    <div className="sprint-modal-overlay" onClick={onClose}>
      <div className="sprint-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="sprint-modal-header">
          <h2>Sprint Management - {boardName}</h2>
          <button className="sprint-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="sprint-modal-body">
          {/* Create/Edit Form */}
          {(showCreateForm || editingSprint) && (
            <div className="sprint-form-section">
              <h3>{editingSprint ? 'Edit Sprint' : 'Create New Sprint'}</h3>
              <form onSubmit={editingSprint ? handleUpdateSprint : handleCreateSprint}>
                <div className="form-row">
                  <div className="form-field">
                    <label>Sprint Name *</label>
                    <input
                      type="text"
                      value={sprintForm.name}
                      onChange={(e) => setSprintForm({ ...sprintForm, name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Start Date *</label>
                    <input
                      type="date"
                      value={sprintForm.start_date}
                      onChange={(e) => setSprintForm({ ...sprintForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>End Date *</label>
                    <input
                      type="date"
                      value={sprintForm.end_date}
                      onChange={(e) => setSprintForm({ ...sprintForm, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Goal</label>
                    <textarea
                      value={sprintForm.goal}
                      onChange={(e) => setSprintForm({ ...sprintForm, goal: e.target.value })}
                      rows="3"
                    />
                  </div>
                  <div className="form-field">
                    <label>Planning Method</label>
                    <select
                      value={sprintForm.planning_method}
                      onChange={(e) => setSprintForm({ ...sprintForm, planning_method: e.target.value })}
                    >
                      <option value="hours">Hours</option>
                      <option value="points">Points</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={sprintForm.is_active}
                        onChange={(e) => setSprintForm({ ...sprintForm, is_active: e.target.checked })}
                      />
                      Set as Active Sprint
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : (editingSprint ? 'Update Sprint' : 'Create Sprint')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingSprint(null);
                      setSprintForm({
                        name: '',
                        start_date: '',
                        end_date: '',
                        goal: '',
                        is_active: false,
                        planning_method: 'hours'
                      });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Sprint List */}
          <div className="sprints-list-section">
            <div className="section-header">
              <h3>All Sprints</h3>
              {!showCreateForm && !editingSprint && (
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowCreateForm(true);
                    setEditingSprint(null);
                  }}
                >
                  + New Sprint
                </button>
              )}
            </div>

            {sprints.length === 0 ? (
              <div className="no-sprints">No sprints created yet. Create your first sprint to get started.</div>
            ) : (
              <div className="sprints-list">
                {sprints.map((sprint) => (
                  <div key={sprint.id} className={`sprint-item ${sprint.is_active ? 'active' : ''}`}>
                    <div className="sprint-item-header">
                      <div>
                        <h4>{sprint.name}</h4>
                        {sprint.is_active && <span className="active-badge">Active</span>}
                      </div>
                      <div className="sprint-item-actions">
                        <button onClick={() => setSelectedSprintForCapacity(sprint)}>Capacity</button>
                        <button onClick={() => handleEditSprint(sprint)}>Edit</button>
                        <button onClick={() => handleDeleteSprint(sprint.id)} className="btn-danger">Delete</button>
                      </div>
                    </div>
                    <div className="sprint-item-dates">
                      {new Date(sprint.start_date).toLocaleDateString()} - {new Date(sprint.end_date).toLocaleDateString()}
                    </div>
                    {sprint.goal && (
                      <div className="sprint-item-goal">{sprint.goal}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {selectedSprintForCapacity && (
        <CapacityManagementModal
          sprintId={selectedSprintForCapacity.id}
          sprintName={selectedSprintForCapacity.name}
          sprintStartDate={selectedSprintForCapacity.start_date}
          sprintEndDate={selectedSprintForCapacity.end_date}
          onClose={() => setSelectedSprintForCapacity(null)}
        />
      )}
    </div>
  );
}

